import { z } from "zod";
import { TerminologyReleaseSchema } from "./orders.js";
import { IdSchema, IsoDateTimeSchema, Sha256Schema } from "./primitives.js";
import { ValidationIssueSchema } from "./validation.js";
export const MedicationDataStatusSchema = z.enum(["synthetic_reference", "authoritative_import"]);
export const RxNormTermTypeSchema = z.enum([
    "IN",
    "PIN",
    "MIN",
    "SCD",
    "SBD",
    "GPCK",
    "BPCK",
    "PSN",
]);
export const MedicationIngredientSchema = z
    .object({
    code: z.string().min(1).max(80),
    display: z.string().min(1).max(240),
})
    .strict();
export const MedicationProductSchema = z
    .object({
    id: IdSchema,
    catalogId: IdSchema,
    catalogVersion: z.number().int().positive(),
    terminologyReleaseId: IdSchema,
    terminologyReleaseVersion: z.number().int().positive(),
    productCode: z.string().min(1).max(80),
    codeSystem: z.literal("RxNorm"),
    termType: RxNormTermTypeSchema,
    display: z.string().min(1).max(240),
    ingredients: z.array(MedicationIngredientSchema).min(1).max(32),
    therapeuticClassCodes: z.array(z.string().min(1).max(80)).max(32).default([]),
    doseForm: z.string().min(1).max(160),
    strength: z
        .object({
        value: z.number().positive(),
        unit: z.string().min(1).max(40),
    })
        .strict(),
    routes: z.array(z.string().min(1).max(80)).min(1).max(20),
    active: z.boolean(),
})
    .strict();
export const MedicationCatalogSchema = z
    .object({
    id: IdSchema,
    tenantId: IdSchema,
    siteId: IdSchema,
    version: z.number().int().positive(),
    terminologyRelease: TerminologyReleaseSchema,
    name: z.string().min(1).max(160),
    dataStatus: MedicationDataStatusSchema,
    status: z.enum(["active", "inactive"]),
    effectiveAt: IsoDateTimeSchema,
    contentHash: Sha256Schema,
    products: z.array(MedicationProductSchema).max(10_000),
})
    .strict()
    .superRefine((value, context) => {
    if (value.terminologyRelease.tenantId !== value.tenantId) {
        context.addIssue({
            code: "custom",
            path: ["terminologyRelease", "tenantId"],
            message: "Medication terminology must belong to the catalogue tenant",
        });
    }
    for (const [index, product] of value.products.entries()) {
        if (product.catalogId !== value.id ||
            product.catalogVersion !== value.version ||
            product.terminologyReleaseId !== value.terminologyRelease.id ||
            product.terminologyReleaseVersion !== value.terminologyRelease.version) {
            context.addIssue({
                code: "custom",
                path: ["products", index],
                message: "Medication product must match its exact catalogue and terminology release",
            });
        }
    }
});
const ProductScopeSchema = z
    .object({
    productCodes: z.array(z.string().min(1).max(80)).min(1).max(100),
})
    .strict();
const AllergyRuleParametersSchema = z
    .object({
    kind: z.literal("allergy"),
    ingredientCodes: z.array(z.string().min(1).max(80)).min(1).max(100),
})
    .strict();
const InteractionRuleParametersSchema = z
    .object({
    kind: z.literal("interaction"),
    productIngredientCodes: z.array(z.string().min(1).max(80)).min(1).max(100),
    interactingIngredientCodes: z.array(z.string().min(1).max(80)).min(1).max(100),
})
    .strict();
const DuplicateTherapyRuleParametersSchema = z
    .object({
    kind: z.literal("duplicate_therapy"),
    therapeuticClassCodes: z.array(z.string().min(1).max(80)).min(1).max(100),
})
    .strict();
const DoseRuleParametersSchema = ProductScopeSchema.extend({
    kind: z.literal("dose"),
    maximumSingleDose: z
        .object({
        value: z.number().positive(),
        unit: z.string().min(1).max(40),
    })
        .strict(),
}).strict();
const RouteRuleParametersSchema = ProductScopeSchema.extend({
    kind: z.literal("route"),
    allowedRoutes: z.array(z.string().min(1).max(80)).min(1).max(20),
}).strict();
const RenalRuleParametersSchema = ProductScopeSchema.extend({
    kind: z.literal("renal"),
    appliesBelowEgfr: z.number().nonnegative(),
    maximumAgeDays: z.number().int().positive().max(3_650),
}).strict();
const HepaticRuleParametersSchema = ProductScopeSchema.extend({
    kind: z.literal("hepatic"),
    prohibitedStatuses: z
        .array(z.enum(["mild_impairment", "moderate_impairment", "severe_impairment"]))
        .min(1),
}).strict();
const PregnancyRuleParametersSchema = ProductScopeSchema.extend({
    kind: z.literal("pregnancy"),
    prohibitedStatuses: z.array(z.enum(["pregnant", "possible"])).min(1),
}).strict();
const AgeRuleParametersSchema = ProductScopeSchema.extend({
    kind: z.literal("age"),
    minimumAgeYears: z.number().nonnegative().optional(),
    maximumAgeYears: z.number().nonnegative().optional(),
})
    .strict()
    .superRefine((value, context) => {
    if (value.minimumAgeYears === undefined && value.maximumAgeYears === undefined) {
        context.addIssue({
            code: "custom",
            message: "An age rule requires a minimum or maximum age",
        });
    }
});
const MonitoringRuleParametersSchema = ProductScopeSchema.extend({
    kind: z.literal("monitoring"),
    observationCodes: z.array(z.string().min(1).max(80)).min(1).max(100),
    maximumAgeDays: z.number().int().nonnegative().max(3_650),
}).strict();
export const MedicationSafetyRuleParametersSchema = z.discriminatedUnion("kind", [
    AllergyRuleParametersSchema,
    InteractionRuleParametersSchema,
    DuplicateTherapyRuleParametersSchema,
    DoseRuleParametersSchema,
    RouteRuleParametersSchema,
    RenalRuleParametersSchema,
    HepaticRuleParametersSchema,
    PregnancyRuleParametersSchema,
    AgeRuleParametersSchema,
    MonitoringRuleParametersSchema,
]);
export const MedicationSafetyRuleSchema = z
    .object({
    id: IdSchema,
    ruleSetId: IdSchema,
    ruleSetVersion: z.number().int().positive(),
    code: z.string().regex(/^[A-Z][A-Z0-9_]{2,99}$/),
    severity: z.enum(["warning", "error", "blocker"]),
    message: z.string().min(1).max(500),
    parameters: MedicationSafetyRuleParametersSchema,
    active: z.boolean(),
})
    .strict();
export const MedicationSafetyRuleSetSchema = z
    .object({
    id: IdSchema,
    tenantId: IdSchema,
    siteId: IdSchema,
    version: z.number().int().positive(),
    name: z.string().min(1).max(160),
    policyVersion: z.string().min(1).max(80),
    dataStatus: MedicationDataStatusSchema,
    productionEligible: z.boolean(),
    status: z.enum(["active", "inactive"]),
    effectiveAt: IsoDateTimeSchema,
    contentHash: Sha256Schema,
    rules: z.array(MedicationSafetyRuleSchema).max(10_000),
})
    .strict()
    .superRefine((value, context) => {
    if (value.productionEligible && value.dataStatus !== "authoritative_import") {
        context.addIssue({
            code: "custom",
            path: ["productionEligible"],
            message: "Synthetic safety content cannot be production eligible",
        });
    }
    for (const [index, rule] of value.rules.entries()) {
        if (rule.ruleSetId !== value.id || rule.ruleSetVersion !== value.version) {
            context.addIssue({
                code: "custom",
                path: ["rules", index],
                message: "Medication safety rule must match its exact rule-set version",
            });
        }
    }
});
export const MedicationConfigurationSchema = z
    .object({
    catalogs: z.array(MedicationCatalogSchema),
    safetyRuleSets: z.array(MedicationSafetyRuleSetSchema),
})
    .strict();
export const MedicationDoseSchema = z
    .object({
    value: z.number().positive(),
    unit: z.string().min(1).max(40),
})
    .strict();
export const StructuredPrescriptionPayloadSchema = z
    .object({
    operation: z.enum(["new", "renew", "change", "cancel"]),
    sourcePrescriptionId: IdSchema.optional(),
    catalogId: IdSchema,
    catalogVersion: z.number().int().positive(),
    terminologyReleaseId: IdSchema,
    terminologyReleaseVersion: z.number().int().positive(),
    productId: IdSchema,
    productCode: z.string().min(1).max(80),
    productCodeSystem: z.literal("RxNorm"),
    productTermType: RxNormTermTypeSchema,
    productDisplay: z.string().min(1).max(240),
    ingredientSnapshot: z.array(MedicationIngredientSchema).min(1).max(32),
    therapeuticClassCodes: z.array(z.string().min(1).max(80)).max(32).default([]),
    dose: MedicationDoseSchema,
    route: z.string().min(1).max(80),
    frequency: z
        .object({
        code: z.string().min(1).max(80),
        display: z.string().min(1).max(160),
    })
        .strict(),
    durationDays: z.number().int().positive().max(3_650).optional(),
    daysSupply: z.number().int().positive().max(3_650).optional(),
    quantity: MedicationDoseSchema,
    refills: z.number().int().min(0).max(99),
    indication: z
        .object({
        code: z.string().min(1).max(80).optional(),
        display: z.string().min(1).max(240),
    })
        .strict(),
    patientDirections: z.string().min(1).max(2_000),
    substitutionPermitted: z.boolean(),
    controlledSchedule: z.enum(["II", "III", "IV", "V"]).optional(),
    pharmacyDestinationId: IdSchema.optional(),
    source: z.enum(["manual", "ai"]),
    evidenceSourceIds: z.array(IdSchema).max(50).default([]),
    safetyRuleSetId: IdSchema,
    safetyRuleSetVersion: z.number().int().positive(),
    safetyPolicyVersion: z.string().min(1).max(80),
})
    .strict()
    .superRefine((value, context) => {
    if (value.source === "ai" && value.evidenceSourceIds.length === 0) {
        context.addIssue({
            code: "custom",
            path: ["evidenceSourceIds"],
            message: "AI-proposed prescriptions require evidence provenance",
        });
    }
    if (value.operation !== "new" && value.sourcePrescriptionId === undefined) {
        context.addIssue({
            code: "custom",
            path: ["sourcePrescriptionId"],
            message: "Renewal, change, and cancellation actions require a source prescription",
        });
    }
    if (value.operation === "new" && value.sourcePrescriptionId !== undefined) {
        context.addIssue({
            code: "custom",
            path: ["sourcePrescriptionId"],
            message: "A new prescription cannot supersede an existing prescription",
        });
    }
});
export const MedicationSafetyContextSchema = z
    .object({
    tenantId: IdSchema,
    siteId: IdSchema,
    encounterId: IdSchema,
    encounterVersion: z.number().int().positive(),
    patientId: IdSchema,
    asOf: IsoDateTimeSchema,
    ageYears: z.number().nonnegative().max(150),
    sexAtBirth: z.enum(["female", "male", "intersex", "unknown"]),
    pregnancyStatus: z.enum(["not_applicable", "not_pregnant", "pregnant", "possible", "unknown"]),
    renalFunction: z
        .object({
        egfr: z.number().nonnegative(),
        observedAt: IsoDateTimeSchema,
        sourceId: IdSchema,
    })
        .strict()
        .optional(),
    hepaticStatus: z.enum([
        "normal",
        "mild_impairment",
        "moderate_impairment",
        "severe_impairment",
        "unknown",
    ]),
    allergiesReviewedAt: IsoDateTimeSchema.optional(),
    medicationsReviewedAt: IsoDateTimeSchema.optional(),
    allergies: z.array(z
        .object({
        id: IdSchema,
        substanceCode: z.string().min(1).max(80),
        display: z.string().min(1).max(240),
        ingredientCodes: z.array(z.string().min(1).max(80)).max(100),
        severity: z.enum(["mild", "moderate", "severe", "unknown"]),
    })
        .strict()),
    medications: z.array(z
        .object({
        id: IdSchema,
        productCode: z.string().min(1).max(80),
        display: z.string().min(1).max(240),
        ingredientCodes: z.array(z.string().min(1).max(80)).max(100),
        therapeuticClassCodes: z.array(z.string().min(1).max(80)).max(100),
        status: z.enum(["active", "inactive", "completed", "unknown"]),
    })
        .strict()),
    observations: z.array(z
        .object({
        id: IdSchema,
        code: z.string().min(1).max(80),
        observedAt: IsoDateTimeSchema,
        value: z.number().optional(),
        unit: z.string().min(1).max(40).optional(),
    })
        .strict()),
    contentHash: Sha256Schema,
})
    .strict();
export const MedicationSafetyCheckSchema = z
    .object({
    code: z.string().min(1).max(100),
    kind: z.enum([
        "catalog",
        "reconciliation",
        "allergy",
        "interaction",
        "duplicate_therapy",
        "dose",
        "route",
        "renal",
        "hepatic",
        "pregnancy",
        "age",
        "monitoring",
        "production_gate",
    ]),
    outcome: z.enum(["pass", "fail", "unknown"]),
    severity: z.enum(["warning", "error", "blocker"]),
    message: z.string().min(1).max(500),
    sourceIds: z.array(IdSchema).default([]),
})
    .strict();
export const MedicationSafetyEvaluationSchema = z
    .object({
    valid: z.boolean(),
    productionEligible: z.boolean(),
    evaluatedAt: IsoDateTimeSchema,
    prescriptionHash: Sha256Schema,
    contextHash: Sha256Schema,
    catalogId: IdSchema,
    catalogVersion: z.number().int().positive(),
    safetyRuleSetId: IdSchema,
    safetyRuleSetVersion: z.number().int().positive(),
    checks: z.array(MedicationSafetyCheckSchema),
    issues: z.array(ValidationIssueSchema),
})
    .strict();
export const ValidatePrescriptionRequestSchema = z
    .object({
    encounterId: IdSchema,
    prescription: StructuredPrescriptionPayloadSchema,
})
    .strict();
//# sourceMappingURL=medications.js.map